import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';

import { CatalogService } from '@shared/services/catalog.service';
import { Country } from '@shared/models/catalog.model';
import { NotificationService } from '@shared/services/notification.service';
import { UnitService } from '../services/unit.service';
import { OrganizationUnit } from '../models/unit.model';
import { OrganizationUnitDto } from '@shared/services/organization-unit.service';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { getApiErrorMessage } from '@core/http/api-error-message.util';
import { UserFullInfoService } from '@core/services/user-full-info.service';

@Component({
    selector: 'app-unit-edit-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatTooltipModule,
        MatExpansionModule,
        MatCardModule,
        MatAutocompleteModule,
        MatOptionModule,
        MatSelectModule
    ],
    templateUrl: './unit-edit-page.component.html',
    styleUrl: './unit-edit-page.component.scss'
})
export class UnitEditPageComponent implements OnInit, OnDestroy {
    @ViewChild('basicPanel') basicPanel!: MatExpansionPanel;
    @ViewChild('buildingPanel') buildingPanel?: MatExpansionPanel;
    @ViewChild('parentAutoTrigger') parentAutoTrigger!: MatAutocompleteTrigger;

    readonly levelOptions = [
        { id: 2, label: 'Área / Apóstol' },
        { id: 3, label: 'Distrito' },
        { id: 4, label: 'Grupo Comunidad' },
        { id: 5, label: 'Comunidad' },
    ];

    unitForm!: FormGroup;
    unitName = 'Nueva Unidad';
    isNewUnit = false;
    isLoading = false;
    isSaving = false;
    errorMessage: string | null = null;
    canManageCommunityData = false;
    countries: Country[] = [];
    filteredCountries: Country[] = [];
    selectedCountry: Country | null = null;
    parentUnitGroups: { levelId: number; levelLabel: string; units: OrganizationUnitDto[] }[] = [];
    isSearchingParent = false;
    private unitId!: string;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private unitService: UnitService,
        private catalogService: CatalogService,
        private notificationService: NotificationService,
        private breadcrumbService: BreadcrumbService,
        private userFullInfoService: UserFullInfoService,
        private cdr: ChangeDetectorRef
    ) {
        this.initializeForm();
        this.userFullInfoService.getUserInfo$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(user => {
                this.canManageCommunityData = this.userFullInfoService.canManageMemberDataSnapshot(user);
                this.cdr.markForCheck();
            });
    }

    // Nivel de comunidad = 5
    get isCommunityLevel(): boolean {
        return this.unitForm?.get('organizationLevelId')?.value === 5;
    }

    get isEditingAddress(): boolean {
        return this.route.snapshot.queryParamMap.get('section') === 'address';
    }

    get selectedLevelId(): number {
        return this.unitForm?.get('organizationLevelId')?.value ?? 0;
    }

    /** True when creating a new unit — all creatable levels (2–5) require a parent */
    get requiresParent(): boolean {
        return this.isNewUnit && this.selectedLevelId > 0;
    }

    get isBasicSectionValid(): boolean {
        const fields = ['organizationLevelId', 'name', 'shortName', 'costCentre', 'description'];
        return fields.every(f => this.unitForm.get(f)?.valid);
    }

    get isBasicSectionTouched(): boolean {
        const fields = ['organizationLevelId', 'name', 'shortName', 'costCentre', 'description'];
        return fields.some(f => this.unitForm.get(f)?.touched);
    }

    get isBuildingSectionValid(): boolean {
        const fields = ['addressLine', 'city', 'department', 'province', 'countryId', 'postalCode', 'latitude', 'longitude', 'additionalInfo'];
        return fields.every(f => this.unitForm.get(f)?.valid);
    }

    get isBuildingSectionTouched(): boolean {
        const fields = ['addressLine', 'city', 'department', 'province', 'countryId', 'postalCode', 'latitude', 'longitude', 'additionalInfo'];
        return fields.some(f => this.unitForm.get(f)?.touched);
    }

    ngOnInit(): void {
        // React to level changes: clear parent unit selection
        this.unitForm.get('organizationLevelId')!.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.unitForm.get('parentUnitId')?.setValue(null);
            this.unitForm.get('parentUnitSearch')?.setValue('');
            this.parentUnitGroups = [];
        });

        // Debounced parent unit search on typing
        this.unitForm.get('parentUnitSearch')!.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntil(this.destroy$),
            switchMap(term => {
                if (!this.requiresParent) return of(null);
                // mat-autocomplete sets the control to the full unit object before (optionSelected) fires — ignore it
                if (typeof term !== 'string') return of(null);
                // If user types something different from the selected unit's name, clear the selection
                const selectedId = this.unitForm.get('parentUnitId')?.value;
                const allUnits = this.parentUnitGroups.flatMap(g => g.units);
                const selectedName = allUnits.find(u => u.id === selectedId)?.name;
                if (selectedId && term === selectedName) {
                    return of(null);
                }
                if (selectedId) {
                    this.unitForm.get('parentUnitId')!.setValue(null, { emitEvent: false });
                }
                this.isSearchingParent = true;
                return this.unitService.searchUnits(term ?? '', this.selectedLevelId - 1).pipe(
                    catchError(() => of(null))
                );
            })
        ).subscribe(result => {
            this.isSearchingParent = false;
            if (result !== null) {
                this.parentUnitGroups = this.groupParentUnits(result?.items ?? []);
                this.parentAutoTrigger?.openPanel();
            }
            this.cdr.markForCheck();
        });

        // Load countries in background — only needed for building section (level 5)
        this.catalogService.getCountries().pipe(
            takeUntil(this.destroy$),
            catchError(error => {
                console.error('Error loading countries:', error);
                return of([]);
            })
        ).subscribe(countries => {
            this.countries = countries;
            this.filteredCountries = countries;
            this.cdr.markForCheck();
        });

        // Load route params (independent of countries)
        this.route.paramMap.pipe(
            takeUntil(this.destroy$),
            switchMap(paramMap => {
                this.unitId = paramMap.get('unit-id')!;
                this.errorMessage = null;
                this.isNewUnit = !this.unitId;

                if (!this.unitId) {
                    this.isNewUnit = true;
                    this.unitName = 'Nueva Unidad';
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    return of(null);
                }

                // Cargar unidad existente
                this.isLoading = true;
                this.unitName = 'Cargando unidad…';
                this.cdr.markForCheck();
                return this.unitService.getUnit(this.unitId).pipe(
                    tap(unit => {
                        this.unitName = unit.name;
                        this.breadcrumbService.setDynamicCrumb('unit-id', unit.name);
                        this.populateForm(unit);
                        this.isLoading = false;
                        this.cdr.markForCheck();
                    }),
                catchError(error => {
                    console.error('Error loading unit:', error);
                    this.errorMessage = getApiErrorMessage(
                        error,
                        'No se pudo cargar la unidad seleccionada'
                    );
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    return of(null);
                })
                );
            })
        ).subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    filterCountries(searchValue: string): void {
        if (!searchValue) {
            this.filteredCountries = this.countries;
            return;
        }

        const search = searchValue.toLowerCase();
        this.filteredCountries = this.countries.filter(country =>
            country.name.toLowerCase().includes(search) ||
            country.iso2Code.toLowerCase().includes(search) ||
            country.iso3Code.toLowerCase().includes(search)
        );
    }

    onCountrySelected(countryId: number): void {
        const selected = this.countries.find(c => c.id === countryId);
        if (selected) {
            this.selectedCountry = selected;
            this.unitForm.get('countryId')?.setValue(countryId);
        }
    }

    private initializeForm(): void {
        this.unitForm = this.fb.group({
            // Nivel y jerarquía
            organizationLevelId: [null, [Validators.required]],
            parentUnitId: [null],
            parentUnitSearch: [''],

            // Datos básicos
            name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
            shortName: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
            costCentre: ['', [Validators.maxLength(100)]],
            description: ['', [Validators.maxLength(500)]],

            // Datos edilicios
            addressLine: ['', [Validators.maxLength(200)]],
            city: ['', [Validators.maxLength(100)]],
            department: ['', [Validators.maxLength(100)]],
            province: ['', [Validators.maxLength(100)]],
            countryId: [0],
            postalCode: ['', [Validators.maxLength(20)]],
            latitude: ['', [Validators.pattern(/^-?\d+(\.\d+)?$/)]],
            longitude: ['', [Validators.pattern(/^-?\d+(\.\d+)?$/)]],
            additionalInfo: ['', [Validators.maxLength(1000)]]
        });
    }

    private populateForm(unit: OrganizationUnit): void {
        // In edit mode: lock the level selector — changing level is not supported
        this.unitForm.get('organizationLevelId')!.setValue(unit.organizationLevelId);
        this.unitForm.get('organizationLevelId')!.disable();

        this.unitForm.patchValue({
            // Datos básicos
            name: unit.name,
            shortName: unit.shortName || '',
            costCentre: unit.costCentre || '',
            description: unit.description || '',

            // Datos edilicios (si existe detail)
            addressLine: unit.detail?.addressLine || '',
            city: unit.detail?.city || '',
            department: unit.detail?.department || '',
            province: unit.detail?.province || '',
            countryId: unit.detail?.countryId || 0,
            postalCode: unit.detail?.postalCode || '',
            latitude: unit.detail?.latitude ? unit.detail.latitude.toString() : '',
            longitude: unit.detail?.longitude ? unit.detail.longitude.toString() : '',
            additionalInfo: unit.detail?.additionalInfo || ''
        });

        // Set selectedCountry if countryId exists
        if (unit.detail?.countryId) {
            this.onCountrySelected(unit.detail.countryId);
        }
    }

    onParentUnitInputFocus(): void {
        if (!this.requiresParent) return;
        // Skip if a unit is already selected (focus returns to input after selection)
        if (this.unitForm.get('parentUnitId')?.value) return;
        const currentTerm = this.unitForm.get('parentUnitSearch')?.value ?? '';
        this.isSearchingParent = true;
        this.unitService.searchUnits(currentTerm, this.selectedLevelId - 1).pipe(
            catchError(() => of(null))
        ).subscribe(result => {
            this.isSearchingParent = false;
            this.parentUnitGroups = this.groupParentUnits(result?.items ?? []);
            this.parentAutoTrigger?.openPanel();
            this.cdr.markForCheck();
        });
    }

    private groupParentUnits(items: OrganizationUnitDto[]): { levelId: number; levelLabel: string; units: OrganizationUnitDto[] }[] {
        const grouped = new Map<number, OrganizationUnitDto[]>();
        for (const unit of items) {
            const levelId = unit.organizationLevelId ?? 0;
            if (!grouped.has(levelId)) grouped.set(levelId, []);
            grouped.get(levelId)!.push(unit);
        }
        return Array.from(grouped.entries())
            .sort(([a], [b]) => a - b)
            .map(([levelId, units]) => ({
                levelId,
                levelLabel: this.levelOptions.find(l => l.id === levelId)?.label ?? `Nivel ${levelId}`,
                units
            }));
    }

    onParentUnitSelected(unit: OrganizationUnitDto): void {
        this.unitForm.get('parentUnitId')!.setValue(unit.id);
        this.unitForm.get('parentUnitSearch')!.setValue(unit.name, { emitEvent: false });
        this.parentUnitGroups = [];
        this.parentAutoTrigger?.closePanel();
    }

    save(): void {
        if (!this.canManageCommunityData) {
            this.notificationService.error('No tenes autorizacion para modificar datos de la comunidad.');
            return;
        }

        // Validate parent unit for non-root levels on create
        if (this.isNewUnit && this.requiresParent && !this.unitForm.get('parentUnitId')?.value) {
            this.unitForm.get('parentUnitSearch')!.markAsTouched();
            this.basicPanel?.open();
            this.notificationService.warning('Por favor, completá todos los campos requeridos correctamente');
            this.focusControlByName('parentUnitSearch');
            return;
        }

        if (!this.unitForm.valid) {
            this.unitForm.markAllAsTouched();

            // Expand panels that contain invalid controls so errors are visible
            const basicFields = ['organizationLevelId', 'name', 'shortName', 'costCentre', 'description'];
            const hasBasicErrors = basicFields.some(f => this.unitForm.get(f)?.invalid);
            if (hasBasicErrors) {
                this.basicPanel?.open();
            }

            const buildingFields = ['addressLine', 'city', 'department', 'province', 'countryId', 'postalCode', 'latitude', 'longitude', 'additionalInfo'];
            const hasBuildingErrors = buildingFields.some(f => this.unitForm.get(f)?.invalid);
            if (hasBuildingErrors) {
                this.buildingPanel?.open();
            }

            this.notificationService.warning('Por favor, completá todos los campos requeridos correctamente');
            this.focusFirstInvalidControl();
            return;
        }

        this.isSaving = true;
        const formValue = this.unitForm.getRawValue();

        if (this.isNewUnit) {
            const createData = {
                name: formValue.name,
                shortName: formValue.shortName,
                costCentre: formValue.costCentre,
                description: formValue.description || null,
                organizationLevelId: formValue.organizationLevelId,
                parentUnitId: formValue.parentUnitId || null,
            };

            this.unitService.createUnit(createData).pipe(
                tap(unit => {
                    this.notificationService.success('Unidad creada correctamente');
                    this.router.navigate(['/units', unit.id]);
                }),
                catchError(error => {
                    console.error('Error creating unit:', error);
                    this.notificationService.error(getApiErrorMessage(
                        error,
                        'No se pudo crear la unidad. Revisá los datos cargados e intentá nuevamente'
                    ));
                    this.isSaving = false;
                    this.cdr.markForCheck();
                    return of(null);
                })
            ).subscribe();
            return;
        }

        // Convertir latitud y longitud a números si están presentes
        const updateData = {
            ...formValue,
            countryId: formValue.countryId || null,
            latitude: formValue.latitude ? parseFloat(formValue.latitude) : null,
            longitude: formValue.longitude ? parseFloat(formValue.longitude) : null
        };

        this.unitService.updateUnit(this.unitId, updateData).pipe(
            tap(() => {
                this.notificationService.success('Unidad actualizada correctamente');
                this.router.navigate(['/units', this.unitId]);
            }),
            catchError(error => {
                console.error('Error updating unit:', error);
                this.notificationService.error(getApiErrorMessage(
                    error,
                    'No se pudo actualizar la unidad. Revisá los datos cargados e intentá nuevamente'
                ));
                this.isSaving = false;
                this.cdr.markForCheck();
                return of(null);
            })
        ).subscribe();
    }

    cancel(): void {
        if (this.unitId) {
            this.router.navigate(['/units', this.unitId]);
        } else {
            this.router.navigate(['/units']);
        }
    }

    goBack(): void {
        this.breadcrumbService.back();
    }

    private focusFirstInvalidControl(): void {
        setTimeout(() => {
            const firstInvalid = document.querySelector(
                'input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid, .mat-mdc-select.ng-invalid, [formcontrolname].ng-invalid'
            ) as HTMLElement | null;

            if (!firstInvalid) {
                return;
            }

            const focusTarget = (firstInvalid.matches('input, textarea, select')
                ? firstInvalid
                : firstInvalid.querySelector('input, textarea, button, [tabindex]')) as HTMLElement | null;

            (focusTarget ?? firstInvalid).focus();
            (focusTarget ?? firstInvalid).scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    private focusControlByName(controlName: string): void {
        setTimeout(() => {
            const control = document.querySelector(`[formcontrolname="${controlName}"]`) as HTMLElement | null;
            if (!control) {
                return;
            }

            const focusTarget = (control.matches('input, textarea, select')
                ? control
                : control.querySelector('input, textarea, button, [tabindex]')) as HTMLElement | null;

            (focusTarget ?? control).focus();
            (focusTarget ?? control).scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }
}
